import type {
  PaginatedProjectTemplatesResponse,
  ProjectTemplate,
  ProjectTemplatePayload,
  ProjectTemplatesResponse,
  ProjectTemplateUpdatePayload,
} from "@/schema/project-template"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"

export async function fetchProjectTemplates(
  context: ApiContext,
  options?: { scope?: "company"; page?: number; search?: string; ownerBookId?: string; includeArchived?: boolean }
): Promise<ProjectTemplatesResponse> {
  const params = new URLSearchParams()
  if (options?.scope) params.set("scope", options.scope)
  if (options?.page) params.set("page", String(options.page))
  if (options?.search) params.set("search", options.search)
  if (options?.ownerBookId) params.set("owner_book_id", options.ownerBookId)
  if (options?.includeArchived) params.set("include_archived", "true")

  const query = params.size > 0 ? `?${params.toString()}` : ""
  return apiContextFetch(context, `/project_templates${query}`)
}

export async function fetchProjectTemplateSummaries(
  context: ApiContext,
  options: { page: number; search?: string; ownerBookId?: string; includeArchived?: boolean }
): Promise<PaginatedProjectTemplatesResponse> {
  const params = new URLSearchParams({ page: String(options.page), summary: "true" })
  if (options.search) params.set("search", options.search)
  if (options.ownerBookId) params.set("owner_book_id", options.ownerBookId)
  if (options.includeArchived) params.set("include_archived", "true")

  return apiContextFetch(context, `/project_templates?${params.toString()}`)
}

export async function fetchProjectTemplate(context: ApiContext, templateId: string): Promise<ProjectTemplate> {
  return apiContextFetch(context, `/project_templates/${templateId}`)
}

export async function createProjectTemplate(
  context: ApiContext,
  payload: ProjectTemplatePayload,
  options?: { scope?: "company" }
): Promise<ProjectTemplate> {
  const params = options?.scope ? `?scope=${options.scope}` : ""
  return apiContextFetch(context, `/project_templates${params}`, {
    method: "POST",
    body: JSON.stringify({ project_template: payload }),
  })
}

export async function updateProjectTemplate(
  context: ApiContext,
  templateId: string,
  payload: ProjectTemplateUpdatePayload
): Promise<ProjectTemplate> {
  return apiContextFetch(context, `/project_templates/${templateId}`, {
    method: "PATCH",
    body: JSON.stringify({ project_template: payload }),
  })
}

export async function updateProjectTemplateFiles(
  context: ApiContext,
  templateId: string,
  newFiles: File[],
  existingSignedIds: string[] = []
): Promise<ProjectTemplate> {
  const apiRoot = (process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/").replace("/api/", "")
  const { uploadFileDirectly } = await import("@/utils/direct-upload")

  const newSignedIds = await Promise.all(newFiles.map((file) => uploadFileDirectly(file, apiRoot, context.token)))
  const allSignedIds = [...existingSignedIds, ...newSignedIds]

  return apiContextFetch(context, `/project_templates/${templateId}`, {
    method: "PATCH",
    body: JSON.stringify({ project_template: { files: allSignedIds } }),
  })
}

export async function renameProjectTemplateAttachment(
  context: ApiContext,
  templateId: string,
  signedId: string,
  fileName: string
): Promise<ProjectTemplate> {
  return apiContextFetch(context, `/project_templates/${templateId}/attachments/${encodeURIComponent(signedId)}`, {
    method: "PATCH",
    body: JSON.stringify({ attachment: { file_name: fileName } }),
  })
}

export async function deleteProjectTemplate(context: ApiContext, templateId: string): Promise<void> {
  await apiContextFetch<unknown>(context, `/project_templates/${templateId}`, {
    method: "DELETE",
  })
}

export interface CloneProjectTemplatePayload {
  book_id?: string
}

export async function cloneProjectTemplate(
  context: ApiContext,
  templateId: string,
  payload: CloneProjectTemplatePayload = {}
): Promise<ProjectTemplate> {
  return apiContextFetch(context, `/project_templates/${templateId}/clone`, {
    method: "POST",
    body: JSON.stringify({ clone: payload }),
  })
}

export interface ReorderProjectTemplatesPayload {
  project_templates: Array<{ id: string; order_index: number }>
}

export async function reorderProjectTemplates(
  context: ApiContext,
  payload: ReorderProjectTemplatesPayload,
  options?: { scope?: "company" }
): Promise<void> {
  const params = options?.scope ? `?scope=${options.scope}` : ""
  await apiContextFetch<unknown>(context, `/project_templates/reorder${params}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  })
}
